import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useState } from 'react';

import type { ImpactResult } from '../../data/impactTypes';
import type { AreaCard } from '../OverviewPanel';
import { MetricsList, SelectedAreasList } from '../OverviewPanel';
import { ProjectionsCard } from '../ProjectionsCard';

import { mobileBottomSheetStyles as styles } from './MobileBottomSheet.styles';

type SheetTab = 'data' | 'selection';

interface MobileBottomSheetProps {
  result: ImpactResult | null;
  resultLoading: boolean;
  resultError: string | null;
  onRetry: () => void;
  amountDollars: number;
  areaCards: AreaCard[];
  maxAreas: number;
  onRemoveArea: (fips: string) => void;
  onClearAll: () => void;
}

/**
 * Mobile-only equivalent of the desktop Overview Panel + Projections Card:
 * a sheet pinned to the bottom of the map, collapsed to just its tab bar by
 * default and expandable (tap the handle) to show the active tab's content.
 * Figma: "d - map - state - selected" mobile frames (iPhone 16/17 Pro).
 */
export function MobileBottomSheet({
  result,
  resultLoading,
  resultError,
  onRetry,
  amountDollars,
  areaCards,
  maxAreas,
  onRemoveArea,
  onClearAll,
}: MobileBottomSheetProps) {
  const [expanded, setExpanded] = useState(false);
  const [tab, setTab] = useState<SheetTab>('data');

  return (
    <Box sx={styles.root(expanded)}>
      <IconButton
        aria-label={expanded ? 'Collapse panel' : 'Expand panel'}
        onClick={() => setExpanded((v) => !v)}
        sx={styles.handleButton}
        disableRipple
      >
        <Box sx={styles.handleBar} />
        {expanded ? (
          <ExpandMoreIcon sx={styles.handleIcon} />
        ) : (
          <ExpandLessIcon sx={styles.handleIcon} />
        )}
      </IconButton>

      <Tabs
        value={tab}
        onChange={(_, next: SheetTab) => {
          setTab(next);
          setExpanded(true);
        }}
        variant="fullWidth"
        sx={styles.tabs}
      >
        <Tab value="data" label="Impact Data" sx={styles.tab} />
        <Tab value="selection" label="Selection" sx={styles.tab} />
      </Tabs>

      <Box sx={styles.content}>
        {tab === 'data' ? (
          <Box sx={styles.section}>
            <ProjectionsCard result={result} resultError={resultError} amountDollars={amountDollars} />
            <MetricsList
              result={result}
              resultLoading={resultLoading}
              resultError={resultError}
              onRetry={onRetry}
            />
          </Box>
        ) : (
          <Box sx={styles.section}>
            <SelectedAreasList
              areaCards={areaCards}
              maxAreas={maxAreas}
              onRemoveArea={onRemoveArea}
              onClearAll={onClearAll}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}
